import { useState } from "react";
import { useTranslations } from "../../../../i18n";
import { api } from "@/lib/api";
import { useStudioStore } from "@/store/studio-store";
import { IconAlertTriangle, IconCircleCheck } from "@tabler/icons-react";
import { Button, Input } from "@heroui/react";
import { useViewStore } from "@/store/view-store";

export default function ForgotPasswordPage() {
  const studio = useStudioStore((state) => state.studio);
  const t = useTranslations("auth");

  const [step, setStep] = useState<"email" | "confirm" | "success">("email");

  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const { navigate } = useViewStore();

  /* ------------------------------------------------------------------
   * STEP 1 — SEND VERIFICATION CODE
   * ------------------------------------------------------------------ */
  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email) {
      setError(t("email-required"));
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      await api("/api/public/widget/password-reset", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          studioId: studio?.id,
        }),
      });

      setStep("confirm");
    } catch (err) {
      console.error(err);
      setError(t("reset-request-failed"));
    } finally {
      setIsLoading(false);
    }
  };

  /* ------------------------------------------------------------------
   * STEP 2 — VERIFY CODE + RESET PASSWORD
   * ------------------------------------------------------------------ */
  const handleConfirmReset = async (e: React.FormEvent) => {
    e.preventDefault();

    if (code.length !== 6) {
      setError(t("invalid-code"));
      return;
    }

    if (newPassword.length < 8) {
      setError(t("password-too-short"));
      return;
    }

    if (newPassword !== confirmPassword) {
      setError(t("passwords-do-not-match"));
      return;
    }

    try {
      setIsLoading(true);
      setError("");

      await api("/api/public/widget/password-reset/confirm", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          code,
          newPassword,
          studioId: studio?.id,
        }),
      });

      setStep("success");
    } catch (err) {
      console.error(err);
      setError(t("reset-failed"));
    } finally {
      setIsLoading(false);
    }
  };

  const goToLogin = () => {
    navigate("login");
  };

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <div className="w-full max-w-md space-y-8 rounded-lg bg-white p-8 shadow-md">
        <div className="text-center">
          <h2 className="text-3xl font-bold text-gray-900">
            {t("forgot-password")}
          </h2>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-md border border-red-500 p-2">
            <IconAlertTriangle className="text-red-500" />
            <p className="text-sm">{error}</p>
          </div>
        )}

        {step === "email" && (
          <form onSubmit={handleSendCode} className="space-y-6">
            <Input
              type="email"
              label={t("email")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("enter-email")}
            />

            <Button
              type="submit"
              color="primary"
              className="h-12 w-full"
              isLoading={isLoading}
            >
              {t("send-reset-code")}
            </Button>

            <div className="text-center">
              <Button
                onPress={goToLogin}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                {t("back-to-login")}
              </Button>
            </div>
          </form>
        )}

        {step === "confirm" && (
          <form onSubmit={handleConfirmReset} className="space-y-6">
            <Input
              label={t("verification-code")}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
              maxLength={6}
              placeholder="123456"
            />

            <Input
              type="password"
              label={t("new-password")}
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
            />

            <Input
              type="password"
              label={t("confirm-password")}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
            />

            <Button
              type="submit"
              color="primary"
              className="h-12 w-full"
              isLoading={isLoading}
            >
              {t("reset-password")}
            </Button>
          </form>
        )}

        {step === "success" && (
          <div className="text-center space-y-4">
            <div className="flex justify-center">
              <IconCircleCheck className="h-16 w-16 text-green-500" />
            </div>
            <p className="text-gray-700">{t("password-reset-success")}</p>
            <Button onPress={goToLogin} color="primary" variant="ghost">
              {t("return-to-login")}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
